/**
 * How wide a value may be before it is broken across lines.
 *
 * Chosen against the shape these documents actually have. A coordinate pair fits, so a footprint
 * reads as a list of points rather than a column of single numbers; a whole ring does not, so the
 * points stay one per line and the shape of the geometry is still visible.
 */
const MAX_INLINE_WIDTH = 60;

const INDENT = '  ';

/** One line, with the spacing `JSON.stringify` omits: `{"a": 1, "b": [2, 3]}`. */
const onOneLine = (value: unknown): string => {
    if (value === null || typeof value !== 'object') {
        return JSON.stringify(value) ?? 'null';
    }

    if (Array.isArray(value)) {
        return `[${value.map(onOneLine).join(', ')}]`;
    }

    return `{${entriesOf(value)
        .map(([name, held]) => `${JSON.stringify(name)}: ${onOneLine(held)}`)
        .join(', ')}}`;
};

/** `undefined` is absent rather than null, which is what `JSON.stringify` does with it. */
const entriesOf = (value: object): [string, unknown][] => Object.entries(value).filter(([, held]) => held !== undefined);

const written = (value: unknown, indent: string): string => {
    const oneLine = onOneLine(value);
    if (value === null || typeof value !== 'object' || indent.length + oneLine.length <= MAX_INLINE_WIDTH) {
        return oneLine;
    }

    const inner = indent + INDENT;
    const parts = Array.isArray(value)
        ? value.map(held => `${inner}${written(held, inner)}`)
        : entriesOf(value).map(([name, held]) => `${inner}${JSON.stringify(name)}: ${written(held, inner)}`);

    const [open, close] = Array.isArray(value) ? ['[', ']'] : ['{', '}'];
    return parts.length === 0 ? `${open}${close}` : `${open}\n${parts.join(',\n')}\n${indent}${close}`;
};

/**
 * A STAC document as text to edit.
 *
 * `JSON.stringify(document, null, 2)` puts every number on its own line, which for geometry is
 * unreadable: the whole-world footprint alone is twenty lines of `-180` and `90`, and a real
 * boundary is thousands. Values short enough to read at a glance are kept on one line instead.
 *
 * Written here rather than taken from a package. The one that does this ships ESM only, and
 * which copy of it is installed is decided by a *transitive* dependency — maplibre asks for a
 * major we do not — so pinning ours does not hold and the build breaks on a reinstall. Thirty
 * lines we own is the smaller liability.
 */
export const asText = (document: unknown): string => written(document, '');
